import type {
  MembershipContractTimeline,
  MembershipSchema
} from '@libs/data-access/entities'
import { membershipService, contractService } from '@libs/data-access/queries'
import { Text, Button, Tooltip, Flex } from '@libs/ui/ds'
import { useMemberPageTranslation } from '@libs/utils/translations'
import { confirmationModalDef } from '@spa/components/Modal/components/customs'
import moment from 'moment'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { fetchPlannablesList } from 'snap-redux/actions/plannables'
import { deleteContract } from 'snap-redux/modules/contracts'
import { useRoleRights } from 'snap-redux/modules/memberships/utils'
import { useActions } from 'snap-redux/utils'

import { SUBMIT_TYPES } from '../../../constants'
import type { ContractFormType } from '../../../types'
import styles from '../../ContractInfoForm.module.css'
import MoveShiftsToNewContractContainer from '../MoveShiftsToNewContractContainer'

import DeleteConfirmMessage from './components/DeleteConfirmMessage'

const nowISO = moment().format('YYYY-MM-DD')

type Props = {
  contract: MembershipContractTimeline | undefined
  membership: MembershipSchema | undefined
  onSubmitTypes(type: string): void
  close: () => void
  setFormError(errors: any): void
  isStartDateUpdated: boolean
  isEndDateUpdated: boolean
  restsToTransfer: number[]
  shiftsToTransfer: number[]
  setShiftsToTransfer(restIds: number[]): void
  setRestsToTransfer(shiftIds: number[]): void
  formLoading: boolean
  onSubmitSuccess?: () => void
}
const SubmitSection: FC<Props> = ({
  contract,
  onSubmitTypes,
  close,
  setFormError,
  isStartDateUpdated,
  isEndDateUpdated,
  restsToTransfer,
  shiftsToTransfer,
  setShiftsToTransfer,
  setRestsToTransfer,
  formLoading,
  onSubmitSuccess,
  membership
}) => {
  const { t, tCommon } = useMemberPageTranslation()
  const {
    control,
    formState: { errors }
  } = useFormContext<ContractFormType>()

  const actions = useActions({
    fetchPlannablesList,
    deleteContract
  })

  const { atLeast } = useRoleRights()
  const { open: openConfirmationModal } = confirmationModalDef.useModal()

  const [isCreatingNewContract, setIsCreatingNewContract] = useState(false)
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState(false)
  const [futureShifts, setFutureShifts] = useState<number[]>([])
  const [isPlannablesLoading, setIsPlannablesLoading] = useState(false)

  // Allow deletion for future and passed contracts
  const isfuturContractStart = contract?.contract_start
    ? contract.contract_start > nowISO
    : false
  const isPassedContractEnd = contract?.contract_end
    ? contract.contract_end <= nowISO
    : false

  const formContractStart = useWatch({ control, name: 'contract_start' })
  const formContractEnd = useWatch({ control, name: 'contract_end' })
  const formContractTime = useWatch({ control, name: 'contract_time' })

  const hasShiftsOrRestsToTransfer = Boolean(
    shiftsToTransfer.length || restsToTransfer.length
  )

  const disableDeleteContract =
    hasShiftsOrRestsToTransfer && isPassedContractEnd

  // For futur contract we need to check if there's some shift in the futur that need to be move/delete
  const shouldDisplayNewContractPopup =
    Boolean(
      hasShiftsOrRestsToTransfer && (isStartDateUpdated || isEndDateUpdated)
    ) ||
    Boolean(
      isfuturContractStart &&
        Boolean(futureShifts?.length) &&
        (isStartDateUpdated || isEndDateUpdated)
    )

  const handleDeleteContract = () => {
    actions.deleteContract(contract?.id).then(() => {
      membershipService.contract.invalidateFetchContracts()
      if (contract?.id) {
        contractService.contract.invalidateOne(contract.id)
      }
      if (onSubmitSuccess) {
        onSubmitSuccess()
      }
      close()
    })
  }

  const displayDeleteConfirmation = () => {
    if (isfuturContractStart) {
      setDeleteConfirmMessage(true)
    }

    if (isPassedContractEnd && atLeast.owner) {
      openConfirmationModal({
        intent: 'delete',
        title: t('contract.info.form.delete.passedContractConfirmation.title', {
          startDate: contract?.contract_start,
          endDate: contract?.contract_end
        }),
        message: (
          <Flex className='gap-3'>
            <Text as='span' size='m'>
              {t(
                'contract.info.form.delete.passedContractConfirmation.content'
              )}
            </Text>
            <Text as='span' size='m' className={styles.DeleteConfirmContent}>
              {t(
                'contract.info.form.delete.passedContractConfirmation.contentBis'
              )}
            </Text>
          </Flex>
        ),
        onConfirm: async () => {
          await handleDeleteContract()
        }
      })
    }
  }
  const hideDeleteConfirm = () => setDeleteConfirmMessage(false)

  const handleDeleteShiftsSubmit = () =>
    onSubmitTypes(SUBMIT_TYPES.DELETE_PLANNABLES)
  const handleMoveToNewContractSubmit = () =>
    onSubmitTypes(SUBMIT_TYPES.MOVE_TO_NEW_CONTRACT)

  useEffect(() => {
    if (
      contract &&
      (isfuturContractStart ||
        isStartDateUpdated ||
        isEndDateUpdated ||
        isPassedContractEnd)
    ) {
      setIsPlannablesLoading(true)
      setShiftsToTransfer([])
      setRestsToTransfer([])

      actions.fetchPlannablesList(
        contract.id,
        {
          ...(isStartDateUpdated && { before_date: formContractStart }),
          ...(isEndDateUpdated && {
            after_date: moment(formContractEnd)
              .add(1, 'day')
              .format('YYYY-MM-DD')
          }),
          ...(isfuturContractStart &&
            !isEndDateUpdated && {
              after_date: moment().add(1, 'day').format('YYYY-MM-DD')
            }),
          ...(isPassedContractEnd &&
            !isEndDateUpdated &&
            !isStartDateUpdated && {
              after_date: contract?.contract_end,
              before_date: moment(contract?.contract_start)
                .add(1, 'day')
                .format('YYYY-MM-DD')
            })
        },
        ({ shift_ids: shiftIds, rest_ids: restIds }) => {
          setIsPlannablesLoading(false)
          if (isfuturContractStart) {
            setFutureShifts(shiftIds)
          } else {
            setShiftsToTransfer(shiftIds)
            setRestsToTransfer(restIds)
          }
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contract,
    formContractEnd,
    formContractStart,
    isStartDateUpdated,
    isEndDateUpdated,
    isPassedContractEnd
  ])

  if (deleteConfirmMessage && contract) {
    return (
      <DeleteConfirmMessage
        close={close}
        contractId={contract.id}
        formLoading={formLoading}
        futureShifts={futureShifts}
        hideDeleteConfirm={hideDeleteConfirm}
        setFormError={setFormError}
        onSubmitSuccess={onSubmitSuccess}
      />
    )
  }

  if (
    shouldDisplayNewContractPopup &&
    contract &&
    formContractStart &&
    formContractEnd
  ) {
    return (
      <MoveShiftsToNewContractContainer
        username={tCommon('models.fullname', {
          value: {
            firstname: membership?.firstname,
            lastname: membership?.lastname
          }
        })}
        contract={contract}
        formContractStart={formContractStart}
        formContractEnd={formContractEnd}
        isStartDateUpdated={isStartDateUpdated}
        isCreatingNewContract={isCreatingNewContract}
        onDeleteShiftsSubmit={handleDeleteShiftsSubmit}
        setIsCreatingNewContract={setIsCreatingNewContract}
        onMoveToNewContractSubmit={handleMoveToNewContractSubmit}
        isLoadingPlannables={isPlannablesLoading}
      />
    )
  }

  return (
    <>
      {((isPassedContractEnd && atLeast.owner) || isfuturContractStart) && (
        <Tooltip
          position='top'
          disabled={!disableDeleteContract}
          label={t('contract.info.form.delete.disableTooltip')}
        >
          <Button
            intent='danger'
            size='m'
            type='button'
            variant='primary'
            fillWidth
            loading={formLoading || isPlannablesLoading}
            disabled={disableDeleteContract}
            onClick={displayDeleteConfirmation}
          >
            {tCommon('action.delete')}
          </Button>
        </Tooltip>
      )}
      <Button
        intent='default'
        size='m'
        type='submit'
        fillWidth
        variant='primary'
        loading={formLoading || isPlannablesLoading}
        disabled={
          Number(formContractTime) > 80 || Object.keys(errors).length > 0
        }
      >
        {tCommon('action.save')}
      </Button>
    </>
  )
}

export default SubmitSection
